/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Define here models used
 * across app that are not defined by the backend
 **/

import { Observable } from 'rxjs';
import { Otp, User } from './auth.models';
import { RequestParams } from './core.models';
import { HttpErrorResponse } from '@angular/common/http';

export type UserProfileData = Pick<
  User,
  | 'firstName'
  | 'lastName'
  | 'gender'
  | 'birthdate'
  | 'countryCode'
  | 'city'
  | 'addresses'
  | 'postBox'
>;

export enum StorageKey {
  LastKnownBrowserLanguage = 'last_known_browser_language',
  PreferredLanguage = 'preferred_language',
  AvailableAccounts = 'available_accounts',
  CurrentUserData = 'current_user_data',
  AuthReturnUrl = 'auth_return_url'
}

export type TimeRangeObject = { startDateTime: string; endDateTime: string };

export type SupportedLanguage = {
  languageCode: string;
  fullLanguageCode: string;
  flag: string;
  name: string;
};

export type FormViolation = {
  propertyPath: string | string[];
  title: string;
  type: string;
};

export type GlobalRouteData = {
  params: Record<string, string>;
  queryParams: Record<string, string>;
  currentUrl: string;
  currentUrlWithoutParams: string;
  previousUrl: string;
  data: Record<string, unknown> | undefined;
};

export type StateFormModel<T> = {
  model: T;
  dirty: boolean;
  status: 'VALID' | 'INVALID' | '';
  errors: unknown | undefined;
  httpErrors: FormGroupError | undefined;
};

export type StateFormWithData<
  T = Record<string, any>,
  F = Partial<T>,
  A = Record<string, any>
> = {
  form: StateFormModel<F | undefined>;
  dataId?: string | undefined;
  data?: Partial<T>;
  additionalData?: A;
};

export type StateStepper = {
  currentStep: number;
  totalSteps: number;
};

export type StateEntity<T, A = Record<string, any>> = {
  data: T;
  loadingErrors: HttpErrorResponse | undefined;
  additionalData?: A;
};

export type GlobalEvent = {
  type:
    | 'TOGGLE_MENU'
    | 'GO_TO_NEXT_STEP'
    | 'GO_TO_PREVIOUS_STEP'
    | 'REFRESH_DATA'
    | 'ONBOARDING_STEP_CHANGED'
    | 'REFRESH_STICKERS_METRICS'
    | 'OPEN_REGISTRATION_2FA_MODAL';
  value?: unknown;
};

// Resource

export type Resource = {
  id: string;
  [key: string]: any;
};

export type ResourceDisposition = 'list' | 'grid';

export type ResourceSelectionMode = 'all' | 'none';

export type SelectData = {
  name: string;
  value: any;
  selected?: boolean;
};

export type ListFilter = {
  requestKey: string;
  requestValue: string | number | Array<any> | null;
  requestFunction?:
    | 'lt'
    | 'lte'
    | 'gt'
    | 'gte'
    | 'range'
    | 'in'
    | 'like'
    | 'notLike'
    | 'eq'
    | 'notEq';
  displayedName: string;
  displayedPlaceholder?: string;
  displayFormatter?: (...args: any) => any;
  inputType?:
    | 'select'
    | 'multi-select'
    | 'number'
    | 'date'
    | 'date-range'
    | 'string'
    | 'autocomplete'
    | 'ng-select';
  selectValues$?: Observable<SelectData[]>;
  autocompleteValues$?: (query?: string | null) => Observable<SelectData[]>;
};

export type FreeRequestParams = RequestParams & { [key: string]: any };

export type FormGroupError = Array<{
  propertyPath: string[];
  title: string;
  type: 'httpError' | string;
  propertyPathIndex: number;
}>;

export type OtpData = Otp & {
  httpError?: FormGroupError;
  receiver?: string;
  field?: 'email' | 'phoneNumber';
  isValid?: boolean | undefined;
  isLoading?: boolean;
  email?: string;
  expiresAt?: string;
  [key: string]: unknown;
};

export type GeoLocalization = {
  lat: number;
  lng: number;
  fullName?: string;
};

export type OpenStreetMapLocation = {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  category: string;
  type: string;
  place_rank: number;
  importance: number;
  addresstype: string;
  name: string;
  display_name: string;
  boundingbox: string[];
};

export type PickItemType<T> = T extends (infer U)[] ? U : never;

export type GeocodingSearchParams = {
  q: string;
  amenity?: string;
  street?: string;
  city?: string;
  county?: string;
  state?: string;
  country?: string;
  postalcode?: string;
  format?: 'xml' | 'json' | 'jsonv2' | 'geojson' | 'geocodejson';
};

export type SharedRequestParams = {
  useStateFormViolationHandler?: boolean;
  skipErrorInterceptor?: boolean;
};

export type OpeningPeriodType = 'everyday' | 'workingDays' | 'custom';

export type Country = {
  name: string;
  code: string;
  dual_code: string;
};

export type StepInfo = {
  title: string;
  description: string;
};

export type OnboardingSignUpStepState = 'number' | 'edit' | 'done';

export type TimeRange = {
  start: string;
  end: string;
};

export enum TimeRangeName {
  TODAY = 'TODAY',
  ANYTIME = 'ANYTIME',
  LAST_THREE_MINUTE = 'LAST_THREE_MINUTE',
  LAST_HOUR = 'LAST_HOUR',
  LAST_DAY = 'LAST_DAY',
  LAST_THREE_DAYS = 'LAST_THREE_DAYS',
  LAST_WEEK = 'LAST_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  LAST_THREE_MONTHS = 'LAST_THREE_MONTHS',
  LAST_YEAR = 'LAST_YEAR',
  CURRENT_WEEK = 'CURRENT_WEEK',
  CURRENT_MONTH = 'CURRENT_MONTH',
  CURRENT_YEAR = 'CURRENT_YEAR',
  CUSTOM = 'CUSTOM'
}
